/**
 * Na uitnodiging (Supabase inviteUserByEmail) moet de gebruiker een wachtwoord kiezen
 * voordat de portal opengaat. Hash bevat vaak type=invite; user_metadata vult aan via Edge.
 */

import type { User } from '@supabase/supabase-js'

const SESSION_HINT_KEY = 'salon_invite_password_pending_hint'

type InviteMeta = {
  account_kind?: unknown
  invite_password_pending?: unknown
}

function readMeta (user: User): InviteMeta {
  return (user.user_metadata ?? {}) as InviteMeta
}

export function captureInviteHashType (): void {
  if (typeof window === 'undefined') return
  const { hash, search } = window.location
  const combined = `${hash}&${search}`
  if (/[?&#]type=invite/i.test(combined) || /type%3Dinvite/i.test(combined)) {
    sessionStorage.setItem(SESSION_HINT_KEY, '1')
  }
}

export function captureRecoveryHashType (): void {
  if (typeof window === 'undefined') return
  const { hash, search } = window.location
  const combined = `${hash}&${search}`
  if (/[?&#]type=recovery/i.test(combined) || /type%3Drecovery/i.test(combined)) {
    sessionStorage.setItem(SESSION_HINT_KEY, '1')
  }
}

export function clearInvitePasswordPendingHint (): void {
  sessionStorage.removeItem(SESSION_HINT_KEY)
}

function needsInvitePasswordSessionFlag (): boolean {
  return sessionStorage.getItem(SESSION_HINT_KEY) === '1'
}

/** Klant na account-uitnodiging: wachtwoord nog verplicht. */
export function needsKlantInvitePasswordSetup (user: User | null): boolean {
  if (!user) return false
  const meta = readMeta(user)
  if (meta.account_kind === 'salon_staff') return false
  if (meta.invite_password_pending === true) return true
  if (meta.account_kind === 'klant' && needsInvitePasswordSessionFlag()) return true
  return false
}

/** Medewerker na team-uitnodiging. */
export function needsStaffInvitePasswordSetup (user: User | null): boolean {
  if (!user) return false
  const meta = readMeta(user)
  if (meta.account_kind === 'klant') return false
  if (meta.invite_password_pending === true) return true
  return needsInvitePasswordSessionFlag() && meta.account_kind === 'salon_staff'
}

/** @deprecated Gebruik needsStaffInvitePasswordSetup of needsKlantInvitePasswordSetup */
export function needsInvitePasswordSetup (user: User | null): boolean {
  return needsStaffInvitePasswordSetup(user) || needsKlantInvitePasswordSetup(user)
}
